import { Combobox, createListCollection } from '@ark-ui/react/combobox';
import { Portal } from '@ark-ui/react/portal';
import {
  useDebouncedSearch,
  useGetOracleProjectQuery,
  useSearchOracleProjectsQuery,
} from '@consigli/hooks';
import { type OracleProject } from '@consigli/types';
import { ChevronDown, X } from 'lucide-react';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UNCHARGEABLE_MESSAGE_KEY } from '@/util/oracle-projects';

/**
 * Every rule below is the design system's own `Select`, read off Select.css and
 * expressed as utilities here. There is no Combobox in the library at 2.3.0, so
 * matching its Select is what keeps a searchable dropdown looking like the
 * dropdown beside it rather than like a second design.
 *
 * Brand colour appears in exactly one place, as it does there: the selected row.
 * Focus and hover are grey — Select.css says in as many words that brand colour
 * no longer signals interaction state.
 */
const CONTROL = 'relative w-full';

const INPUT = [
  'w-full h-[var(--ds-space-9)] box-border',
  'px-[var(--ds-space-3)] pr-[var(--ds-space-10)]',
  'rounded-[var(--ds-radius-8)] border border-[var(--ds-color-border-primary)]',
  'bg-[var(--ds-color-bg-primary)] text-[color:var(--ds-color-text-primary)]',
  'text-[length:var(--ds-typography-paragraph-13-regular-font-size)]',
  'leading-[var(--ds-typography-paragraph-13-regular-line-height)]',
  'font-[var(--ds-typography-paragraph-weight)] outline-none',
  'placeholder:text-[color:var(--ds-color-text-secondary)]',
  'hover:not-disabled:border-[var(--ds-color-border-secondary)]',
  'focus-visible:outline focus-visible:outline-[length:var(--ds-space-0-5)]',
  'focus-visible:outline-[var(--ds-color-border-primary)]',
  'focus-visible:outline-offset-[var(--ds-space-0-5)]',
  'disabled:cursor-not-allowed disabled:bg-[var(--ds-color-bg-secondary)]',
  'disabled:text-[color:var(--ds-color-text-disabled)]',
].join(' ');

/**
 * Sits inside the input's right padding, as Select's indicator does.
 *
 * Both the clear and the chevron are buttons, and a button's default cursor is
 * an arrow — so neither read as something to click. `pointer`, and
 * `not-allowed` when disabled, is what the design system's own Select trigger
 * does. Shared by the two because having one of them look clickable and the
 * other not would be worse than either choice.
 */
const ADORNMENT =
  'absolute inset-y-0 flex cursor-pointer items-center text-[color:var(--ds-color-bg-icon-primary)] disabled:cursor-not-allowed disabled:text-[color:var(--ds-color-text-disabled)]';

const CONTENT = [
  'z-50 w-[var(--reference-width)] box-border',
  'max-h-[min(var(--available-height),var(--ds-space-75))] overflow-y-auto overscroll-y-none',
  'rounded-[var(--ds-radius-8)] bg-[var(--ds-color-bg-primary)] outline-none',
  // Select.css's two-layer 5% black, composed the same way.
  'shadow-[0_var(--ds-space-1)_var(--ds-space-3)_0_color-mix(in_srgb,var(--ds-color-raw-black)_5%,transparent),0_var(--ds-space-0-5)_var(--ds-space-1)_0_color-mix(in_srgb,var(--ds-color-raw-black)_5%,transparent)]',
].join(' ');

const ITEM = [
  'flex w-full items-center gap-[var(--ds-space-2)] box-border',
  'min-h-[var(--ds-space-10)] px-[var(--ds-space-3)] py-[var(--ds-space-2)]',
  'border-b border-[var(--ds-color-border-primary)] last:border-b-0',
  'bg-[var(--ds-color-bg-primary)] text-[color:var(--ds-color-raw-stone-600)]',
  'text-[length:var(--ds-typography-paragraph-mini-font-size)]',
  'leading-[var(--ds-typography-paragraph-mini-line-height)]',
  'cursor-pointer outline-none',
  'data-[highlighted]:bg-[var(--ds-color-bg-secondary-dark)]',
  'data-[state=checked]:text-[var(--ds-color-bg-brand)]',
  'data-[disabled]:cursor-not-allowed data-[disabled]:text-[color:var(--ds-color-text-disabled)]',
].join(' ');

/** A row of the menu: the number in medium, an em dash, then the name. */
interface Row {
  disabled: boolean;
  /** Why it cannot be picked, when it cannot. */
  disabledReason?: string;
  label: string;
  number: string;
}

const rowText = (row: Row): string => `${row.number} — ${row.label}`;

interface OracleProjectFieldProps {
  /** Id of the helper text above, for the input's `aria-describedby`. */
  describedBy?: string;
  disabled?: boolean;
  /**
   * The input's accessible name. The visible label beside it is a `<span>`, so
   * it names nothing on its own — the same reason `Input` and `Select` take
   * their name as a prop here.
   */
  label: string;
  onChange: (projectNumber: string | null) => void;
  /**
   * The picked Oracle project, or `null` when the field is cleared. The create
   * form reads its name, customer and type class code from here. Not called for
   * the stored value the field resolves on open.
   */
  onProjectChange?: (project: OracleProject | null) => void;
  /** The stored `oracleId`, which *is* an Oracle project number. */
  value: string;
}

/**
 * The Oracle project picker, shared by the project Settings tab and the create
 * project form. In components/ rather than beside either of them, per the
 * promote-on-second-consumer norm agents-loading.tsx records.
 *
 * A searchable list rather than a free-text box, because `oracleId` is not a
 * free-text field: the value has to name a project Oracle knows, or the
 * technology charge it exists to carry has nowhere to land. Both frames draw a
 * plain box with "( max 20 characters )" under it — 11759:160478 for Settings
 * and 11914:189989 for create — and both ship this instead, so a number typed
 * into one surface cannot be a number the other refuses.
 *
 * Ark UI's Combobox, not the facade's: the facade is the legacy library, and
 * Ark is what the design system itself is built on, so the keyboard handling,
 * dismissal and positioning here are the same ones the Select beside it uses.
 *
 * The chrome reuses the `project-creation.oracle-*` strings. The namespace reads
 * a little off under Settings, and that is the trade: they are already
 * translated in all eleven locales and say exactly this, down to the hint. The
 * same call project-invites-table.tsx makes with `user-management.invite-status.*`.
 *
 * A stored number is resolved to its name through `getOracleProject`, so the
 * field opens reading "03001027 — AECOM Controls" rather than a bare number.
 * Until that resolves — or if it fails, which it will for a project the account
 * cannot see — the number stands alone, which is still the truth about what is
 * stored.
 */
export const OracleProjectField: FC<OracleProjectFieldProps> = ({
  describedBy,
  disabled = false,
  label,
  onChange,
  onProjectChange,
  value,
}) => {
  const { t } = useTranslation();
  /**
   * What the user has typed, or `null` when they are not searching at all.
   *
   * `''` and `null` are different states and the field breaks if they are
   * conflated. `''` is a box the user emptied and it must stay empty. `null` is
   * a box nobody is typing in, which shows the stored project. Reading an empty
   * string as "not searching" handed the stored text back to Ark on the
   * keystroke that deleted the last character, so the field could not be
   * cleared from the keyboard at all.
   */
  const [query, setQuery] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const typed = query ?? '';
  const debouncedQuery = useDebouncedSearch(typed, 500);

  const { currentData, isFetching, isError, error } = useSearchOracleProjectsQuery(
    { search: debouncedQuery },
    { skip: !debouncedQuery || accessDenied || disabled },
  );

  const is403 = isError && (error as { status?: number })?.status === 403;

  if (is403 && !accessDenied) {
    setAccessDenied(true);
  }

  // Names the stored number. Skipped while searching, which has its own rows.
  const { data: storedProject } = useGetOracleProjectQuery(
    { projectNumber: value },
    { skip: value === '' },
  );

  const rows = useMemo(
    (): Row[] =>
      (currentData?.results ?? []).map((project: OracleProject) => ({
        disabled: !project.chargeable,
        disabledReason: project.unchargeableReason
          ? t(UNCHARGEABLE_MESSAGE_KEY[project.unchargeableReason])
          : undefined,
        label: project.projectName,
        number: project.projectNumber,
      })),
    [currentData, t],
  );

  const collection = useMemo(
    () =>
      createListCollection({
        isItemDisabled: (row: Row) => row.disabled,
        items: rows,
        itemToString: rowText,
        itemToValue: (row: Row) => row.number,
      }),
    [rows],
  );

  /** What the closed field reads: the stored project, named if Oracle knows it. */
  const storedText = useMemo(() => {
    if (value === '') return '';
    return storedProject === undefined ? value : `${value} — ${storedProject.projectName}`;
  }, [storedProject, value]);

  const clear = useCallback(() => {
    onChange(null);
    onProjectChange?.(null);
  }, [onChange, onProjectChange]);

  const onValueChange = useCallback(
    ({ value: next }: { value: string[] }) => {
      const projectNumber = next[0] ?? null;
      onChange(projectNumber);
      onProjectChange?.(
        currentData?.results.find((project) => project.projectNumber === projectNumber) ?? null,
      );
    },
    [currentData, onChange, onProjectChange],
  );

  const isSearching = isFetching || (typed !== '' && typed !== debouncedQuery);
  const hasQuery = typed.trim() !== '';

  return (
    <Combobox.Root
      collection={collection}
      disabled={disabled}
      // Typing filters the remote list, so Ark must not also filter locally.
      inputValue={query ?? storedText}
      // Only what the user types drives the box. Ark reports the value it
      // writes itself under the same callback — on a pick, on the clear button,
      // on a click outside — and each of those ends the search, which is what
      // `null` means. Without the reason, choosing a project would leave its
      // label sitting in the box as a live search term.
      onInputValueChange={({ inputValue, reason }) => {
        if (reason !== 'input-change') {
          setQuery(null);
          return;
        }

        setQuery(inputValue);

        // Emptying the box clears the field, exactly as the clear button does.
        // The two are the same intent and the keyboard is the faster of them.
        // Leaving the value in place instead would put it back on blur, which
        // reads as the field refusing to be cleared.
        //
        // Trimmed, so a box holding a space clears too. It looks empty, and an
        // untrimmed check would restore the project the moment focus left.
        if (inputValue.trim() === '') clear();
      }}
      // Dismissing the menu ends the search. Escape reaches the field only
      // here, because it closes the menu without moving focus or touching the
      // input.
      //
      // Opening on a click undoes the selection `onFocus` makes: Ark moves the
      // caret to the end one frame later, so the first keystroke landed after
      // the stored project again. Its frame is queued after this callback runs,
      // so a single frame here would lose to it. Two frames select after it.
      // Typing opens the menu too, and must not select what was just typed.
      onOpenChange={({ open, reason }) => {
        if (!open) {
          setQuery(null);
          return;
        }

        if (reason === 'input-click' && query === null) {
          requestAnimationFrame(() => requestAnimationFrame(() => inputRef.current?.select()));
        }
      }}
      onValueChange={onValueChange}
      openOnClick
      // The stored value is not in `collection` until a search finds it, and a
      // value Ark cannot resolve would otherwise be dropped on the next render.
      selectionBehavior="replace"
      value={value === '' ? [] : [value]}
    >
      <Combobox.Control className={CONTROL}>
        <Combobox.Input
          aria-describedby={describedBy}
          aria-label={label}
          className={INPUT}
          // Leaving the field ends the search, so the box goes back to
          // reporting what is stored. An abandoned search term is not a value —
          // nothing is written until the user picks a row — and leaving it on
          // screen would say the form is about to save it.
          //
          // An emptied box has nothing to go back to, because emptying it
          // cleared the field. That is handled above, on the keystroke.
          //
          // Blur rather than the menu closing, because the menu is not reliably
          // open by the time the user leaves — emptying the box can close it.
          // Picking a row does not blur the input: Ark keeps focus there.
          onBlur={() => setQuery(null)}
          // The input holds the stored project whenever nobody is searching, so
          // a keystroke lands after it: typing "maple" searched Oracle for
          // "6824704270 — Devonshire ParkM", then "…ParkMa", and matched
          // nothing all the way down. Selecting the text means the first
          // keystroke replaces it, which is what typing into a filled search
          // box is for.
          //
          // The mouseup guard is what carries it through a click. Focus runs
          // first, and mouseup would then drop the caret where the pointer
          // landed and throw the selection away.
          //
          // Both stop once a search starts, so the user can still put the caret
          // inside a term they are part-way through typing.
          onFocus={(event) => {
            if (query === null) event.currentTarget.select();
          }}
          onMouseUp={(event) => {
            if (query === null) event.preventDefault();
          }}
          placeholder={t('project-creation.oracle-project-placeholder')}
          ref={inputRef}
        />

        {value !== '' && !disabled ? (
          <Combobox.ClearTrigger
            aria-label={t('project-overview.settings-oracle-clear')}
            className={`${ADORNMENT} right-[var(--ds-space-3)]`}
            onClick={() => {
              setQuery(null);
              clear();
            }}
          >
            <X className="size-4" />
          </Combobox.ClearTrigger>
        ) : (
          <Combobox.Trigger
            aria-label={t('project-creation.oracle-project-label')}
            className={`${ADORNMENT} right-[var(--ds-space-3)]`}
            disabled={disabled}
          >
            <ChevronDown className="size-4" />
          </Combobox.Trigger>
        )}
      </Combobox.Control>

      <Portal>
        <Combobox.Positioner>
          <Combobox.Content className={CONTENT}>
            {isError ? (
              <p className={ITEM}>
                {t(
                  is403
                    ? 'project-creation.oracle-not-authorized'
                    : 'project-creation.oracle-project-search-error',
                )}
              </p>
            ) : isSearching ? (
              <p className={ITEM}>{t('project-creation.oracle-project-searching')}</p>
            ) : hasQuery && rows.length === 0 ? (
              <p className={ITEM}>{t('project-creation.oracle-project-no-results')}</p>
            ) : (
              rows.map((row) => (
                <Combobox.Item className={ITEM} item={row} key={row.number}>
                  <Combobox.ItemText>
                    <span className="font-medium">{row.number}</span>
                    {' — '}
                    {row.label}
                    {row.disabled && row.disabledReason !== undefined && (
                      <span className="ml-1 text-[color:var(--ds-color-text-disabled)]">
                        ({row.disabledReason})
                      </span>
                    )}
                  </Combobox.ItemText>
                </Combobox.Item>
              ))
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};
